/** The app's language, set by i18n.ts whenever it changes (also at the start) */
let currentLanguage = "en";

export const setNumberLanguage = (language: string) => {
  currentLanguage = language;
};

/**
 * The browser locale of an app language: "pt_BR" -> "pt-BR", "gr" (Greek) -> "el". With Latin digits in every
 * language, only the separator changes (Arabic or Bengali would have their own digits)
 */
const toLocale = (language: string) =>
  `${language === "gr" ? "el" : language.replace("_", "-")}-u-nu-latn`;

const numberFormats = new Map<string, Intl.NumberFormat>();

const getNumberFormat = (language: string, decimals: number) => {
  const key = `${language}/${decimals}`;
  let numberFormat = numberFormats.get(key);
  if (!numberFormat) {
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      // Without thousands separators, like before (2438 m): with them 2.438 m in German could be read as 2.4 m
      useGrouping: false,
    };
    try {
      numberFormat = new Intl.NumberFormat(toLocale(language), options);
    } catch {
      // Not a valid locale
      numberFormat = new Intl.NumberFormat("en-u-nu-latn", options);
    }
    numberFormats.set(key, numberFormat);
  }
  return numberFormat;
};

/**
 * A number with the decimal separator of the app's language: 2.5 in English, 2,5 in Slovak, German, French, ...
 * Rounded to `decimals` like `toFixed`. `language` is the app's language by default; pass it where the result is
 * memoized, so it's formatted again when the language changes
 */
export const formatNumber = (
  value: number,
  decimals: number,
  language: string = currentLanguage
) => getNumberFormat(language, decimals).format(value);
